import {
   ApiResponse,
   createApiRequest,
   getHeaderFromSessionStorage,
   Header,
} from "@/api"
import { API_URL, Endpoints } from "@/constants/endpoints"
import { HttpMethods } from "@/constants/globals"

export interface SensorResponse {
   id: string
   serialNumber: string
   macAddress: string
   level: string
}

export enum InmateCustodyStatus {
   IN_CUSTODY = "InCustody", // "in_custody", use for test purposes
   RELEASED = "Released",
}

export interface InmateResponse {
   id: string
   customIdentifier: string
   externalId: string
   firstName: string
   middleName?: string | null
   lastName: string
   sensor: SensorResponse | null
   bookingNumber?: string | null
   inmateNumber?: string | null
   housingUnit?: string | null
   housingBlock?: string | null
   housingCell?: string | null
   custodyStatus?: InmateCustodyStatus | null
}

export interface InmatesPaginatedResponse {
   results: InmateResponse[]
}

export interface LevelInmateSensorResponse {
   id: string
   macAddress: string
   serialNumber: string
}

export interface LevelInmateResponse {
   id: string
   customIdentifier: string
   firstName: string
   lastName: string
   sensor: LevelInmateSensorResponse | null
   location: string | null
}

export interface InmateVitalsResponse {
   averages: {
      heartRate: number | null
      spo2: number | null
      temperature: number | null
   }
   heartRate: number
   heartRateConfidence: number
   spo2: number
   spo2Confidence: number
   anchorMac: string
   batteryPercent: number
   temperature: number
   readTime: string
}

export interface InmateExportRequest {
   inmate: string
   startDate: string
   endDate: string
   timezone?: string
}

export interface InmateLogsExportRequest {
   inmate: string
   startDate: string
   endDate: string
   timezone?: string
   alarmsOnly?: boolean
}

export interface InmateExportResponse {
   id: string
   startDate: string
   endDate: string
   status: string
   progress: number
   createdAt: string
   expiresAt: string | null
   isExpired: boolean
   inmate: {
      id: string
      customIdentifier: string
   }
   requestedBy: string
}

export interface InmateLogsExportResponse {
   id: string
   status: string
}

export async function requestInmatesData(): Promise<
   ApiResponse<InmatesPaginatedResponse>
> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.INMATES}`,
      HttpMethods.GET,
      null,
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}

export async function requestLevelInmates(
   levelId: string
): Promise<ApiResponse<LevelInmateResponse[]>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.LEVELS}${levelId}/inmates/`,
      HttpMethods.GET,
      null,
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}

export async function requestInmate(
   id: string
): Promise<ApiResponse<InmateResponse>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.INMATES}${id}/`,
      HttpMethods.GET,
      null,
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}

export async function requestAssignWearable(
   inmateId: string,
   wearableId: string
): Promise<ApiResponse<void>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.INMATES}${inmateId}/wearable/`,
      HttpMethods.PATCH,
      { sensor: wearableId },
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}

export async function requestCreateInmate(
   inmateData: Partial<InmateResponse>
): Promise<ApiResponse<InmateResponse>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.INMATES}`,
      HttpMethods.POST,
      inmateData,
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}

export async function requestDeleteInmate(
   id: string
): Promise<ApiResponse<null>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.INMATES}${id}/`,
      HttpMethods.DELETE,
      null,
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}

export async function requestUpdateInmate(
   id: string,
   inmateData: Partial<InmateResponse>
): Promise<ApiResponse<InmateResponse>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.INMATES}${id}/`,
      HttpMethods.PATCH,
      inmateData,
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}

export async function requestInmateVitals(
   inmateId: string
): Promise<ApiResponse<InmateVitalsResponse>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.INMATES}${inmateId}/vitals/`,
      HttpMethods.GET,
      null,
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}

export async function requestExportInmateData(
   exportRequest: InmateExportRequest
): Promise<ApiResponse<InmateExportResponse>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.INMATES}export/`,
      HttpMethods.POST,
      exportRequest,
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}

export async function requestExportInmateLogs(
   exportRequest: InmateLogsExportRequest
): Promise<ApiResponse<InmateLogsExportResponse>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.INMATES}logs/export/`,
      HttpMethods.POST,
      exportRequest,
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}
